import * as React from 'react';

import { plural } from '@cian/utils/lib/shared/plural';
import { Button } from '@cian/ui-kit';
import { numberToPrettyString } from '@cian/utils';

import { createVacanciesFromResponse, IVacancyFromResponse } from '../../mappers/helpers';

import { IVacancy } from '../../index';
import { trackingDeleteVacancy, trackingSearchVacancies } from '../../tracking/tracking';
import { scrollTop } from '../../utils';
import { ConfirmModal } from '../confirm_modal/confirm_modal';
import { Pagination } from '../pagination/pagination';
import { PreloaderWrapper } from '../preloader_wrapper/preloader_wrapper';
import { RequestError } from '../request_error/request_error';
import { ISearchFilter, VacanciesFilter } from '../vacancies_filter/vacancies_filter';
import { VacancyList } from '../vacancy_list/vacancy_list';
import { IHttpApi } from '@cian/http-api/shared';
import { IUser } from '../../types/user';
import { getVacancies } from '../../services/getVacancies';
import { ILogger } from '@cian/logger';
import { ESubdomain } from '../../constants/domain';
import { deleteVacancy } from '../../services/deleteVacancy';

import * as styles from './vacancies.css';
import { IRegion } from '../../types/region';

interface IVacanciesProps {
  httpApi: IHttpApi;
  logger: ILogger;
  vacancies: IVacancy[];
  vacanciesCount: number;
  pageCount: number;
  regionsWithVacancy: IRegion[];
  filter: ISearchFilter;
  user: IUser;
}

interface IVacanciesState {
  deleteVacancyModalOpened: boolean;
  reviewModalOpened: boolean;
  requestError: boolean;
  vacancyIdForDelete?: number;
  processing: boolean;
  vacancies: IVacancy[];
  vacanciesCount: number;
  pageCount: number;
}

export const formatSearchQuery = (filter: ISearchFilter) =>
  (filter.region ? `region=${filter.region}` : '') +
  (filter.minSalary ? `&minsalary=${filter.minSalary}` : '') +
  (filter.maxSalary ? `&maxsalary=${filter.maxSalary}` : '') +
  (filter.companyName ? `&companyname=${encodeURIComponent(filter.companyName)}` : '') +
  (filter.p ? `&p=${filter.p}` : '');

export const getVacanciesUrl = (filter: ISearchFilter) => `/vacancies?${formatSearchQuery(filter)}`;

const searchVacancies = (filter: ISearchFilter) => {
  const searchQuery = formatSearchQuery(filter);
  trackingSearchVacancies(searchQuery);
  window.location.search = `?${searchQuery}`;
};

export class Vacancies extends React.Component<IVacanciesProps, IVacanciesState> {
  public constructor(props: IVacanciesProps) {
    super(props);

    this.state = {
      deleteVacancyModalOpened: false,
      pageCount: props.pageCount,
      processing: false,
      requestError: false,
      reviewModalOpened: false,
      vacancies: props.vacancies,
      vacanciesCount: props.vacanciesCount,
    };
  }

  public render() {
    return (
      <div>
        <VacanciesFilter
          regionsWithVacancy={this.props.regionsWithVacancy}
          onSearch={searchVacancies}
          filter={this.props.filter}
        />

        <div className={styles['container']}>
          <div className={styles['list_head']}>
            <div className={styles['vacancies_count']}>
              {numberToPrettyString(this.state.vacanciesCount)}{' '}
              {plural(this.state.vacanciesCount, ['вакансия', 'вакансии', 'вакансий'])}{' '}
              {plural(this.state.vacanciesCount, ['отсортирована', 'отсортированы', 'отсортированы'])} по дате создания
            </div>
            <Button
              data-name="PublishButton"
              size="XS"
              theme="fill_primary"
              onClick={() => (window.location.href = '/vacancies/add')}
            >
              + Опубликовать вакансию бесплатно
            </Button>
          </div>
          {!this.state.requestError && (
            <VacancyList
              vacancies={this.state.vacancies}
              user={this.props.user}
              editBackUrl={getVacanciesUrl(this.props.filter)}
              deleteVacancy={this.wantDeleteVacancy}
              logger={this.props.logger}
              httpApi={this.props.httpApi}
            />
          )}
          {this.state.requestError && <RequestError />}
          <div className={styles['footer']}>
            <div>
              {this.state.pageCount > 1 && (
                <Pagination
                  pageCount={this.state.pageCount}
                  filter={this.props.filter}
                  onPageChanged={this.paginationChanged}
                />
              )}
            </div>
          </div>
          <ConfirmModal
            isOpen={this.state.deleteVacancyModalOpened}
            onClose={() => this.setState({ deleteVacancyModalOpened: false })}
            onConfirm={this.deleteVacancy}
            headText="Удалить вакансию?"
            confirmText="Удалить"
            dismissText="Отмена"
          />
        </div>
        {this.state.processing && <PreloaderWrapper size={40} />}
      </div>
    );
  }

  private paginationChanged = (page: number) => {
    const { httpApi, logger } = this.props;
    const filter: ISearchFilter = Object.assign(this.props.filter, { p: page });
    this.setState({ processing: true });

    window.scrollTo(0, 0);
    window.history.replaceState({}, '', getVacanciesUrl(filter));

    getVacancies({ httpApi, logger, subdomain: ESubdomain.Vacancies }, { filter })
      .then(response => {
        scrollTop();

        this.setState({
          pageCount: response.pageCount as number,
          processing: false,
          vacancies: createVacanciesFromResponse(
            response.vacancies as IVacancyFromResponse[],
            this.props.regionsWithVacancy,
          ),
          vacanciesCount: response.vacancyCount as number,
        });
      })
      .catch(() => {
        this.setState({
          pageCount: 0,
          processing: false,
          requestError: true,
          vacanciesCount: 0,
        });
      });
  };

  private deleteVacancy = () => {
    if (!this.state.vacancyIdForDelete) {
      this.setState({
        deleteVacancyModalOpened: false,
      });

      return;
    }
    const { httpApi, logger } = this.props;

    deleteVacancy({ httpApi, logger, subdomain: ESubdomain.Vacancies }, { vacancyId: this.state.vacancyIdForDelete })
      .then(() => window.location.reload())
      .catch(() => {});

    this.setState({
      deleteVacancyModalOpened: false,
      vacancyIdForDelete: undefined,
    });
  };

  private wantDeleteVacancy = (id: number) => {
    trackingDeleteVacancy(id);
    this.setState({
      deleteVacancyModalOpened: true,
      vacancyIdForDelete: id,
    });
  };
}
